import { storeContainer } from 'src/models/Store'
import { IS_BROWSER, convertToBoolean, getLocalStorage } from 'src/utils'
import { pageNames } from 'src/routes/pathParams'
import { APPConfig } from 'config/appConfig'
import { getLocaleCodeFromUrl } from './localeUtils'

const isSubscriptionOverrideEnabled = () => {
  try {
    const getSubscriptionOverrideProperty = (storeResponse = {}) => {
      return storeResponse?.properties?.find(
        property => property?.name === 'subscriptionOverride'
      )?.value
    }
    const activeStoreValue = getSubscriptionOverrideProperty(
      storeContainer?.activeStoreResponse
    )
    const globalStoreValue = getSubscriptionOverrideProperty(
      storeContainer?.globalStoreResponse
    )

    const subscriptionOverrideProperty =
      (activeStoreValue && JSON.parse(activeStoreValue)) ||
      (globalStoreValue && JSON.parse(globalStoreValue)) ||
      {}

    return subscriptionOverrideProperty
  } catch (e) {
    console.error('Error parsing storeConfig for subscription override', e)
    return false
  }
}

const isToEnableSubscriptionOverride = () => {
  try {
    const subscriptionOverrideProperty = isSubscriptionOverrideEnabled() || {}
    const enableSubscriptionOverrideFeature =
      subscriptionOverrideProperty?.enableSubscriptionOverride || false

    return convertToBoolean(enableSubscriptionOverrideFeature)
  } catch (e) {
    console.error('Error parsing storeConfig for subscription override', e)
    return false
  }
}

const isToSkipSubscriptionOrderForOverride = () => {
  try {
    const subscriptionOverrideProperty = isSubscriptionOverrideEnabled() || {}
    const skipSubscriptionOrderForOverride =
      subscriptionOverrideProperty?.skipMasterSubscription || false

    return convertToBoolean(skipSubscriptionOrderForOverride)
  } catch (e) {
    console.error('Error parsing storeConfig for subscription override', e)
    return false
  }
}

const isSubscriptionOverridePages = () => {
  try {
    if (IS_BROWSER && typeof window !== 'undefined') {
      const isSubscriptionOverrideCartPage =
        window?.location?.pathname?.includes?.(pageNames.subscriptionCart) ||
        window?.location?.pathname?.includes?.(pageNames.subscriptionCheckout)
      return isSubscriptionOverrideCartPage
    }
    return false
  } catch (e) {
    console.error('IsSubscriptionOverride Page Condition Error', e)
    return false
  }
}

const isToShowOverrideSubscription = () => {
  const isSubscriptionOverrideEnabled = isToEnableSubscriptionOverride()
  const isSubscriptionOverridePage = isSubscriptionOverridePages()
  return isSubscriptionOverrideEnabled && isSubscriptionOverridePage
}

const isSubscriptionOverrideCheckoutPage = () => {
  try {
    if (IS_BROWSER && typeof window !== 'undefined') {
      const isSubscriptionOverrideEnabled = isToEnableSubscriptionOverride()
      const isSubscriptionOverrideCheckoutPage =
        window?.location?.pathname?.includes?.(pageNames?.subscriptionCheckout)
      return isSubscriptionOverrideEnabled && isSubscriptionOverrideCheckoutPage
    }
    return false
  } catch (e) {
    console.error('isSubscriptionOverrideCheckoutPage Page Condition Error', e)
    return false
  }
}

const getTimeZone = () => {
  return APPConfig?.getAppConfig()?.marketTimeZone || 'US/Mountain'
}

const enableTimeZoneDateConversion = () => {
  return (
    convertToBoolean(
      APPConfig?.getAppConfig()?.enableTimeZoneDateFormat?.toString()
    ) || false
  )
}

const formatDate = (date, options) => {
  const curLocale =
    getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
    }) || 'en_US'
  const locale = curLocale.replace('_', '-') || 'en-US'
  const timeZone = getTimeZone()
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date)
}

const getBlackoutDates = () => {
  return APPConfig?.getAppConfig()?.blackoutDates ?? ['29', '30', '31']
}

const getSubscriptionActiveTabName = props => {
  const { isFromMultipleSubscriptions = false, activeTab = '' } = props || {}
  const subscriptionTab = getLocalStorage('subscriptionActiveTab') || ''
  const subscriptionTabName =
    subscriptionTab === 'schedule' ? 'subscription schedule' : 'next orders'
  const subscriptionActiveTabName = isFromMultipleSubscriptions
    ? activeTab
    : subscriptionTabName
  return subscriptionActiveTabName
}

const getSubscriptionLimitConfig = () => {
  const authorizationLimitConfig =
    storeContainer.activeStoreResponse?.properties?.find(
      property => property?.name === 'subscriptionsConfig'
    )?.value

  if (!authorizationLimitConfig) {
    return {}
  }

  return typeof authorizationLimitConfig === 'string'
    ? JSON.parse(authorizationLimitConfig)
    : authorizationLimitConfig || {}
}

const getSubscriptionAuthorizedLimit = () => {
  try {
    const { maxGrandTotal } = getSubscriptionLimitConfig() || {}
    return Number(maxGrandTotal) || 0
  } catch (e) {
    console.error('Error parsing subscriptionsConfig for authorized limit', e)
    return 0
  }
}

export {
  isToEnableSubscriptionOverride,
  isSubscriptionOverridePages,
  isToShowOverrideSubscription,
  isSubscriptionOverrideCheckoutPage,
  isToSkipSubscriptionOrderForOverride,
  isSubscriptionOverrideEnabled,
  getBlackoutDates,
  getSubscriptionActiveTabName,
  getTimeZone,
  formatDate,
  getSubscriptionLimitConfig,
  getSubscriptionAuthorizedLimit,
  enableTimeZoneDateConversion,
}
